/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import usuarioService from '../services/usuarioService';
import servicioService from '../services/servicioService';
import { toast } from 'react-toastify';
import ConfirmModal from '../components/modals/ConfirmModal';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUsuario, setCurrentUsuario] = useState({
    name: '',
    lastName: '',
    email: '',
    profilePicture: '',
    services: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [serviciosPorUsuario, setServiciosPorUsuario] = useState({});

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const pageSize = 20;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userSelected, setUserSelected] = useState(null);

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    role: '',
    fromDate: '',
    toDate: '',
    minBusiness: '',
    maxBusiness: '',
    search: ''
  });

  // Memorizar las dependencias para evitar cambios de tamaño en useEffect
  const filtrosDependencias = useMemo(() => {
    return JSON.stringify(filtros);
  }, [filtros]);

  // Cargar usuarios cuando cambie la página o filtros
  useEffect(() => {
    fetchUsuarios();
  }, [currentPage, filtrosDependencias]);

  // Cargar servicios al montar el componente
  useEffect(() => {
    const cargarServicios = async () => {
      try {
        const contadorServicios = await servicioService.getServiciosPorUsuario();
        setServiciosPorUsuario(contadorServicios);
      } catch (error) {
        toast.error('Error al cargar servicios');
      }
    };

    cargarServicios();
  }, []);

  // Función para obtener partners con paginación y filtros
  const fetchUsuarios = async () => {
    try {
      setLoading(true);

      // Usar el nuevo endpoint optimizado con paginación y filtros
      const response = await usuarioService.getPartners(currentPage, pageSize, filtros);
      console.log('Datos recibidos del backend:', response);

      // El endpoint ya devuelve solo partners, no necesitamos filtrar
      const usuariosData = response.data || response.partners || response.users || [];

      // Actualizar información de paginación
      const totalPagesFromResponse = response.totalPages || response.pagination?.totalPages || Math.ceil((response.total || response.pagination?.total || 0) / pageSize);
      const totalUsuariosFromResponse = response.total || response.pagination?.total || response.count || 0;

      console.log('Información de paginación extraída:', {
        totalPages: totalPagesFromResponse,
        totalUsuarios: totalUsuariosFromResponse,
        currentPage: currentPage
      });

      setTotalPages(totalPagesFromResponse);
      setTotalUsuarios(totalUsuariosFromResponse);

      setUsuarios(usuariosData);
      setError(null);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setError('No se pudieron cargar los usuarios. Por favor, intenta de nuevo más tarde.');
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Funciones de navegación de paginación
  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  // Función para manejar cambios en los filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
    setCurrentPage(1); // Resetear a la primera página al cambiar filtros
  };

  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      role: '',
      fromDate: '',
      toDate: '',
      minBusiness: '',
      maxBusiness: '',
      search: ''
    });
    setCurrentPage(1);
  };

  // Función para abrir el modal en modo creación
  const handleAddUsuario = () => {
    setCurrentUsuario({
      name: '',
      lastName: '',
      email: '',
      profilePicture: '',
      services: []
    });
    setIsEditing(false);
    setModalOpen(true);
  };

  // Función para guardar un usuario (crear o actualizar)
  const handleSaveUsuario = async () => {
    try {
      if (isEditing) {
        await usuarioService.updateUsuario(currentUsuario.id, currentUsuario);
      } else {
        await usuarioService.createUsuario(currentUsuario);
      }
      setModalOpen(false);
      fetchUsuarios();
    } catch (err) {
      toast.error('Error al guardar el usuario. Por favor, intenta de nuevo.');
    }
  };

  const handleOpenConfirmModal = (id) => {
    setUserSelected(id);
    setShowConfirmModal(true);
  }

  // Función para eliminar un usuario
  const handleDeleteUser = async () => {
    if (!userSelected) return toast.error('No se ha seleccionado ningún usuario para eliminar');
    try {
      setLoading(true);
      await usuarioService.deleteUsuario(userSelected);
      const nuevosUsuarios = usuarios.filter(usuario => usuario.id !== userSelected);
      setUsuarios(nuevosUsuarios);
      toast.success('Usuario eliminado exitosamente');
    } catch (err) {
      toast.error('Error al eliminar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setUserSelected(null);
  }

  // Función para manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentUsuario({ ...currentUsuario, [name]: value });
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Usuarios de Xervix</h2>
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-gray-600 mb-4">
          Aquí podrás administrar todos los usuarios registrados en el sistema.
        </p>
        <div className="mt-6">
          <button
            onClick={handleAddUsuario}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Agregar Usuario
          </button>
          <button className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
            Exportar Datos
          </button>
        </div>

        {/* Filtros */}
        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-medium text-gray-700 mb-4">Filtros</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Filtro de búsqueda */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar usuario
              </label>
              <input
                type="text"
                placeholder="Nombre o email..."
                value={filtros.search}
                onChange={(e) => handleFiltroChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por rol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol
              </label>
              <select
                value={filtros.role}
                onChange={(e) => handleFiltroChange('role', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los roles</option>
                <option value="inmobiliaria">Inmobiliaria</option>
                <option value="administradorPH">Administrador PH</option>
                <option value="user">Usuario</option>
              </select>
            </div>

            {/* Filtro por fecha desde */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha desde
              </label>
              <input
                type="date"
                value={filtros.fromDate}
                onChange={(e) => handleFiltroChange('fromDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por fecha hasta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha hasta
              </label>
              <input
                type="date"
                value={filtros.toDate}
                onChange={(e) => handleFiltroChange('toDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por business mínimo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business mínimo (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Ej: 10"
                value={filtros.minBusiness}
                onChange={(e) => handleFiltroChange('minBusiness', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por business máximo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business máximo (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Ej: 50"
                value={filtros.maxBusiness}
                onChange={(e) => handleFiltroChange('maxBusiness', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Botón para limpiar filtros */}
          <div className="mt-4">
            <button
              onClick={limpiarFiltros}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
            >
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* Estado de carga y error */}
        {loading && <p className="mt-4 text-gray-600">Cargando usuarios...</p>}
        {error && <p className="mt-4 text-red-500">{error}</p>}

        {/* Tabla de usuarios */}
        {!loading && !error && (
          <div className="overflow-x-auto mt-6">
            <table className="min-w-full bg-white text-center">
              <thead>
                <tr className="w-full h-16 border-gray-300 border-b py-8">
                  <th className="text-left px-6 font-bold text-gray-600">Nombre</th>
                  <th className="text-left px-6 font-bold text-gray-600">Email</th>
                  <th className="text-left px-6 font-bold text-gray-600">Servicios</th>
                  <th className="text-left px-6 font-bold text-gray-600">Rol</th>
                  <th className="text-center px-6 font-bold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios?.map(usuario => (
                  <tr key={usuario.id} className="h-16 border-gray-300 border-b hover:bg-gray-50">

                    <td className="text-left py-3 px-6 ">{usuario.name || usuario.nombre || '-'}</td>

                    <td className="text-left py-3 px-6 ">{usuario.email}</td>
                    <td className="text-left py-3 px-6 ">
                      <span className="text-left py-1 px-3 bg-gray-100 text-gray-700 rounded-full text-xs">
                        {serviciosPorUsuario[usuario.id] || 0}
                      </span>
                    </td>
                    <td className="text-left py-3 px-6">{usuario.role}</td>

                    <td className="py-3 px-6 text-center">
                      <div className="flex item-center justify-center">
                        <button
                          onClick={() => handleOpenConfirmModal(usuario.id)}
                          className="text-red-500 hover:text-red-700 mx-1"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Controles de paginación */}
        {!loading && !error && totalPages > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando página {currentPage} de {totalPages} ({totalUsuarios} usuarios en total)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                ««
              </button>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                ‹ Anterior
              </button>
              <span className="px-3 py-1 bg-gray-100 rounded">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                Siguiente ›
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                »»
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal para crear/editar usuario */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {isEditing ? 'Editar Usuario' : 'Agregar Nuevo Usuario'}
            </h3>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="nombre">
                Nombre
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={currentUsuario.nombre}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={currentUsuario.email}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="rol">
                Rol
              </label>
              <select
                id="rol"
                name="rol"
                value={currentUsuario.rol}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="Administrador">Administrador</option>
                <option value="Usuario">Usuario</option>
                <option value="Invitado">Invitado</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setModalOpen(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveUsuario}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleDeleteUser}
        title={"Eliminar Usuario"}
        message={"¿Estás seguro de que deseas eliminar este usuario?"}
        confirmText={"Eliminar"}
        cancelText="Cancelar"
        confirmButtonColor={"bg-red-600 hover:bg-red-700"}
      />
    </div>
  );
};

export default Usuarios;
